import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import {
    useDownloadApnFileMutation,
  useGetAllApnListMutation,
  useUpdateApnFileMutation,
} from "../../../Services/admin/api.admin.slice";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box,
  CardHeader,
  Avatar,
  Divider,
  Button,
  Stack,
  Tooltip,
  Grid,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import { Download } from "@mui/icons-material";

const APNUploadPage = () => {
  const [apnList, setApnList] = useState<any[]>([]);
  const [comments, setComments] = useState<{ [key: string]: string }>({});
  const [statuses, setStatuses] = useState<{ [key: string]: string }>({});
  const [getAllApnList] = useGetAllApnListMutation();
    const [updateApnFile, { isLoading: isUpdating }] = useUpdateApnFileMutation();
    const [downloadApnFile] = useDownloadApnFileMutation();

  const fetchApnList = useCallback(async () => {
    try {
      const response: any = await getAllApnList({}).unwrap();
      setApnList(response.data?.result || []);
    } catch (error) {
      console.error("Error fetching APN list:", error);
    }
  }, [getAllApnList]);

  useEffect(() => {
    fetchApnList();
  }, [fetchApnList]);

  const handleCommentChange = (apnId: string, value: string) => {
    setComments((prev) => ({ ...prev, [apnId]: value }));
  };

  const handleStatusChange = (apnId: string, value: string) => {
    setStatuses((prev) => ({ ...prev, [apnId]: value }));
  };

  const handleUpdate = async (apnId: string) => {
    try {
      await updateApnFile({
        apnId,
        body: {
          status: statuses[apnId],
          comments: comments[apnId],
        },
      }).unwrap();
      fetchApnList();
      setComments((prev) => ({ ...prev, [apnId]: "" }));
      setStatuses((prev) => ({ ...prev, [apnId]: "" }));
    } catch (error) {
      alert("Failed to update APN details.");
    }
  };

    const downloadAPNDocument = async (fileName: string) => {
        try {
            const response: any = await downloadApnFile(fileName).unwrap();
            if (response?.data?.preSignedUrl) {
                window.open(response?.data?.preSignedUrl, '_blank');
            }
        }
        catch (error) {
            alert("Failed to download APN document.");
        }
  };

  const renderPropertyDetails = (property: any) => {
    if (!property) return "-";
    return (
      <Box sx={{ fontSize: 14, color: "text.secondary" }}>
        <div>
          <strong>Address:</strong> {property.address || "-"}
        </div>
        <div>
          <strong>City:</strong> {property.city || "-"}
        </div>
        <div>
          <strong>State:</strong> {property.state || "-"}
        </div>
        <div>
          <strong>Zip:</strong> {property.zip || "-"}
        </div>
        <div>
          <strong>County:</strong> {property.county || "-"}
        </div>
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      {apnList.length === 0 ? (
        <Box sx={{ textAlign: "center", mt: 8, color: "text.secondary" }}>
          <Typography variant="h6">No data found</Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {apnList.map((apn) => (
            <Grid item xs={12} sm={6} md={4} key={apn._id}>
              <Card
                sx={{
                  boxShadow: 4,
                  borderRadius: 4,
                  minHeight: 370,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: "#1976d2" }}>
                      <HomeIcon />
                    </Avatar>
                  }
                  title={
                    <Stack direction={"row"}>
                      <Typography variant="button" sx={{ flex: 1 }}>
                        {apn.propertyId?.propertyInfo?.address?.address}
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          p: 0,
                          minWidth: 0,
                        }}
                        onClick={() => downloadAPNDocument(apn.fileName)}
                      >
                        <Tooltip title="Download APN Document" placement="top">
                          <Download
                            fontSize="inherit"
                            style={{ fontSize: 20 }}
                          />
                        </Tooltip>
                      </Button>
                    </Stack>
                  }
                  titleTypographyProps={{ fontWeight: 600, fontSize: 18 }}
                  sx={{
                    bgcolor: "#f5faff",
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                  }}
                />
                <Divider />
                <CardContent sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    <strong>User:</strong> {apn.userId?.email || "-"}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ mt: 1, mb: 0.5, color: "#1976d2" }}
                  >
                    Property Details
                  </Typography>
                  {renderPropertyDetails(apn.propertyId?.propertyInfo?.address)}

                  {/* List previous comments */}
                  {Array.isArray(apn.cancelledReason) &&
                    apn.cancelledReason.length > 0 && (
                      <Box mt={2} mb={1}>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: "#d32f2f" }}
                        >
                          Previous Comments
                        </Typography>
                        {[...apn.cancelledReason]
                          .sort(
                            (a, b) =>
                              new Date(b.date).getTime() -
                              new Date(a.date).getTime()
                          )
                          .map((item: any, idx: number) => (
                            <Box
                              key={idx}
                              sx={{
                                mb: 1,
                                p: 1,
                                bgcolor: "#fff3e0",
                                borderRadius: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontStyle: "italic" }}
                              >
                                "{item.comment}"
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {item.admin ? `By: ${item.admin}` : ""}{" "}
                                {item.date
                                  ? `• ${moment(item.date).fromNow()}`
                                  : ""}
                              </Typography>
                            </Box>
                          ))}
                      </Box>
                    )}

                  <Box mt={2}>
                    <TextField
                      label="Comments"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={comments[apn._id] || ""}
                      onChange={(e) =>
                        handleCommentChange(apn._id, e.target.value)
                      }
                      placeholder="Enter comments"
                    />
                  </Box>
                  <Box mt={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        label="Status"
                        value={statuses[apn._id] || ""}
                        onChange={(e) =>
                          handleStatusChange(apn._id, e.target.value)
                        }
                      >
                        <MenuItem value="">Select</MenuItem>
                        <MenuItem value="accepted">Accepted</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box mt={2} display="flex" justifyContent="flex-END">
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={!comments[apn._id] || !statuses[apn._id]}
                      onClick={() => handleUpdate(apn._id)}
                      fullWidth
                    >
                      {isUpdating ? "Updating..." : "Update"}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default APNUploadPage;
